<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import {
  TemplateFieldType,
  type AnnotationFilter,
  type AnnotationInstance,
  type FieldCatalog,
  type FieldCatalogEntity,
  type FieldCatalogField,
  type TemplateAnnotation,
} from '@/api';
import { TemplateAuthoringService } from '@/services/contractGeneration/templateAuthoringService';
import {
  renderJinjaPreview,
  type AnnotationDraft,
} from '@/composables/contractGeneration/useTemplateAnnotations';
import {
  controlFlowKindLabel,
  controlFlowSubLabel,
  newConditionalSnippet,
  newLoopSnippet,
  renderControlFlowSnippet,
  type ControlFlowSnippet,
} from '@/composables/contractGeneration/useControlFlowSnippets';
import FieldPalette from '@/components/contractGeneration/templateAuthoring/FieldPalette.vue';
import AnnotationConfigPopover from '@/components/contractGeneration/templateAuthoring/AnnotationConfigPopover.vue';
import ControlFlowConfigDrawer from '@/components/contractGeneration/templateAuthoring/ControlFlowConfigDrawer.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { useGaiaToast } from '@/composables/useGaiaToast';
import {
  gaiaFontSize,
  gaiaFonts,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaShellPadding,
  gaiaStatus,
  gaiaTemplating,
} from '@/components/design/themes/tokens';

/**
 * Standalone snippet library — independent of any document. Users click a
 * tile in the palette, configure the field in the same drawer the authoring
 * view uses, and save it as a copy-paste-able Jinja snippet. State is
 * session-only: snippets and entity/object aliases live in component refs
 * and reset on page reload. Fully isolated from the per-document authoring
 * flow — nothing here ever hits the backend.
 */

const message = useGaiaToast();

const catalog = ref<FieldCatalog | null>(null);
const loading = ref<boolean>(true);
const loadError = ref<string | null>(null);

/** Saved field snippets, in insertion order. Newest at the bottom. */
const snippets = ref<AnnotationDraft[]>([]);

/** Saved control-flow snippets (conditionals + loops), in insertion order. */
const controlFlowSnippets = ref<ControlFlowSnippet[]>([]);

/**
 * Entity/object aliases minted while building snippets. Used as the source
 * for the drawer's alias dropdown so the user can reuse a role across
 * snippets. Derived on save, never sent anywhere.
 */
const sessionInstances = ref<AnnotationInstance[]>([]);

// Drawer state ----------------------------------------------------------
const drawerVisible = ref<boolean>(false);
const drawerDraft = ref<AnnotationDraft | null>(null);
const drawerFieldType = ref<TemplateFieldType | null>(null);
/** When set, the drawer is editing an existing snippet rather than creating one. */
const editingLocalId = ref<string | null>(null);
/** Initial state of the drawer's "Don't show in document" switch on open. */
const drawerInitialHidden = ref<boolean>(false);
/**
 * Local ids of snippets the user marked "Don't show in document" — rendered
 * as non-printing `{% set _ = … %}` instead of `{{ … }}`.
 */
const hiddenIds = ref<Set<string>>(new Set());

/** Turn a `{{ expr }}` snippet into a non-printing `{% set _ = expr %}`. */
function toHiddenSet(jinja: string): string {
  // Capture the inner expression of a `{{ … }}` tag, trimmed of surrounding
  // whitespace — e.g. `{{ age | format_number("en") }}` → `age | format_number("en")`.
  const match = jinja.match(/^\{\{\s*([\s\S]*?)\s*\}\}$/);
  const expr = match ? match[1] : jinja;
  return expr ? `{% set _ = ${expr} %}` : jinja;
}

/** Render a field snippet, applying the hidden `{% set %}` wrap when marked. */
function renderFieldSnippet(snippet: AnnotationDraft): string {
  const base = renderJinjaPreview(snippet);
  return hiddenIds.value.has(snippet.local_id) ? toHiddenSet(base) : base;
}

// Control-flow drawer state -------------------------------------------
const cfDrawerVisible = ref<boolean>(false);
const cfDraft = ref<ControlFlowSnippet | null>(null);
/** When set, the control-flow drawer is editing an existing snippet. */
const cfEditingLocalId = ref<string | null>(null);

// Copy-feedback state per snippet card.
const copiedAt = ref<Record<string, number>>({});
const copyFeedbackTimers: Record<string, number> = {};

// --- Catalog loading -------------------------------------------------

async function loadCatalog(): Promise<void> {
  loading.value = true;
  loadError.value = null;
  try {
    catalog.value = await TemplateAuthoringService.getFieldCatalog();
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : 'Failed to load field catalog.';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  void loadCatalog();
});

// --- Helpers (mirrors the per-document authoring view) ---------------

function makeLocalId(): string {
  return `snippet-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Stub anchor — the drawer doesn't actually read it for snippet authoring,
 * but `AnnotationDraft` (and downstream type checks) require one to be set.
 */
function stubAnchor(): AnnotationDraft['anchor'] {
  return {
    scope: { kind: 'body' },
    paragraph_index: 0,
    run_index: 0,
    offset_in_run: 0,
    snippet_before: '',
    snippet_after: '',
  };
}

function defaultFieldNameForPrimitive(type: TemplateFieldType): string {
  switch (type) {
    case TemplateFieldType.STRING:
      return 'text_field';
    case TemplateFieldType.NUMBER:
      return 'number_field';
    case TemplateFieldType.DATE:
      return 'date_field';
    case TemplateFieldType.SELECTION:
      return 'selection_field';
    case TemplateFieldType.BOOLEAN:
      return 'flag';
    default:
      return 'field';
  }
}

/**
 * Same default filters the per-document drag-drop path applies. Keeps the
 * shape of newly created snippets aligned with what a dropped chip would
 * look like, so users learn one mental model.
 */
function defaultFiltersForPrimitive(type: TemplateFieldType): AnnotationFilter[] {
  if (type === TemplateFieldType.DATE) return [{ name: 'format_date', args: ['en', 'long'] }];
  if (type === TemplateFieldType.NUMBER) return [{ name: 'format_number', args: ['en'] }];
  return [];
}

function defaultAliasForEntity(entityType: 'legal_entity' | 'natural_person'): string {
  const existing = sessionInstances.value.find(
    inst => inst.kind === 'entity' && inst.entity_type === entityType
  );
  if (existing) return existing.alias;
  return entityType === 'legal_entity' ? 'company' : 'person';
}

function defaultAliasForObject(): string {
  const existing = sessionInstances.value.find(inst => inst.kind === 'object');
  return existing?.alias ?? 'data';
}

/**
 * Derive a field-type hint from a snippet for the drawer's filter dropdown
 * scoping. For entity bindings we look the sub-field up in the catalog; for
 * primitives we ask the snippet's `selection_options` (selection) or fall
 * back to the existing filters' applicable_types intersection.
 */
function deriveFieldTypeForSnippet(snippet: AnnotationDraft): TemplateFieldType | null {
  const binding = snippet.binding;
  if (binding?.kind === 'entity') {
    const entity = catalog.value?.entities.find(e => e.name === binding.entity_type);
    const subField = entity?.fields.find(f => f.name === binding.sub_field);
    return subField?.type ?? null;
  }
  if (snippet.selection_options && snippet.selection_options.length > 0) {
    return TemplateFieldType.SELECTION;
  }
  const filters = snippet.filters ?? [];
  if (filters.length === 0) return null;
  const filterDefs = catalog.value?.filters ?? [];
  const perFilter = filters
    .map(f => filterDefs.find(d => d.name === f.name)?.applicable_types)
    .filter((arr): arr is TemplateFieldType[] => Array.isArray(arr) && arr.length > 0);
  if (perFilter.length === 0) return null;
  const intersection = perFilter.reduce<TemplateFieldType[]>(
    (acc, types) => acc.filter(t => types.includes(t)),
    [...perFilter[0]]
  );
  return intersection.length === 1 ? intersection[0] : null;
}

// --- Palette handlers ------------------------------------------------

function openDraft(draft: AnnotationDraft, fieldType: TemplateFieldType | null): void {
  drawerDraft.value = draft;
  drawerFieldType.value = fieldType;
  editingLocalId.value = null;
  drawerInitialHidden.value = false;
  drawerVisible.value = true;
}

function handlePickPrimitive(type: TemplateFieldType): void {
  const draft: AnnotationDraft = {
    local_id: makeLocalId(),
    kind: 'inline',
    anchor: stubAnchor(),
    field_name: defaultFieldNameForPrimitive(type),
    binding: { kind: 'primitive' },
    origin: 'user',
    filters: defaultFiltersForPrimitive(type),
    required: false,
  };
  openDraft(draft, type);
}

function handlePickEntityField(entity: FieldCatalogEntity, field: FieldCatalogField): void {
  const entityType = entity.name as 'legal_entity' | 'natural_person';
  const draft: AnnotationDraft = {
    local_id: makeLocalId(),
    kind: 'inline',
    anchor: stubAnchor(),
    field_name: null,
    binding: {
      kind: 'entity',
      entity_type: entityType,
      instance_alias: defaultAliasForEntity(entityType),
      sub_field: field.name,
    },
    origin: 'user',
    filters: [],
    required: false,
  };
  openDraft(draft, field.type);
}

function handlePickObjectField(): void {
  const draft: AnnotationDraft = {
    local_id: makeLocalId(),
    kind: 'inline',
    anchor: stubAnchor(),
    field_name: null,
    binding: {
      kind: 'object',
      instance_alias: defaultAliasForObject(),
      sub_field: 'field',
    },
    origin: 'user',
    filters: [],
    required: false,
  };
  openDraft(draft, null);
}

// --- Control-flow palette + CRUD -------------------------------------

function handlePickConditional(): void {
  cfDraft.value = newConditionalSnippet();
  cfEditingLocalId.value = null;
  cfDrawerVisible.value = true;
}

function handlePickLoop(): void {
  cfDraft.value = newLoopSnippet();
  cfEditingLocalId.value = null;
  cfDrawerVisible.value = true;
}

function handleApplyControlFlow(snippet: ControlFlowSnippet): void {
  if (cfEditingLocalId.value) {
    const targetId = cfEditingLocalId.value;
    controlFlowSnippets.value = controlFlowSnippets.value.map(s =>
      s.local_id === targetId ? snippet : s
    );
  } else {
    controlFlowSnippets.value = [...controlFlowSnippets.value, snippet];
  }
  cfEditingLocalId.value = null;
}

function editControlFlowSnippet(snippet: ControlFlowSnippet): void {
  cfDraft.value = snippet;
  cfEditingLocalId.value = snippet.local_id;
  cfDrawerVisible.value = true;
}

function handleRemoveActiveControlFlow(): void {
  if (!cfEditingLocalId.value) return;
  removeControlFlowSnippet(cfEditingLocalId.value);
  cfEditingLocalId.value = null;
}

function removeControlFlowSnippet(localId: string): void {
  controlFlowSnippets.value = controlFlowSnippets.value.filter(s => s.local_id !== localId);
}

// --- Snippet CRUD ----------------------------------------------------

function captureAlias(draft: AnnotationDraft): void {
  const binding = draft.binding;
  if (!binding) return;
  if (binding.kind !== 'entity' && binding.kind !== 'object') return;

  const alias = binding.instance_alias.trim();
  if (!alias) return;

  const exists = sessionInstances.value.some(inst => {
    if (inst.kind !== binding.kind) return false;
    if (binding.kind === 'entity' && inst.kind === 'entity') {
      return inst.entity_type === binding.entity_type && inst.alias === alias;
    }
    return inst.alias === alias;
  });
  if (exists) return;

  const subField = binding.sub_field;
  const newInstance: AnnotationInstance =
    binding.kind === 'entity'
      ? {
          kind: 'entity',
          entity_type: binding.entity_type,
          alias,
          used_sub_fields: subField ? [subField] : [],
        }
      : {
          kind: 'object',
          entity_type: null,
          alias,
          used_sub_fields: subField ? [subField] : [],
        };
  sessionInstances.value = [...sessionInstances.value, newInstance];
}

function handleApply(patch: Partial<TemplateAnnotation>, options?: { hidden?: boolean }): void {
  if (!drawerDraft.value) return;

  const merged: AnnotationDraft = {
    ...drawerDraft.value,
    ...patch,
  };

  // Track the "don't show in document" choice so the card renders as a
  // `{% set %}` snippet.
  const nextHidden = new Set(hiddenIds.value);
  if (options?.hidden) nextHidden.add(merged.local_id);
  else nextHidden.delete(merged.local_id);
  hiddenIds.value = nextHidden;

  if (editingLocalId.value) {
    const targetId = editingLocalId.value;
    snippets.value = snippets.value.map(s => (s.local_id === targetId ? merged : s));
  } else {
    snippets.value = [...snippets.value, merged];
  }
  captureAlias(merged);
  editingLocalId.value = null;
}

function handleRemoveActive(): void {
  if (!editingLocalId.value) return;
  const targetId = editingLocalId.value;
  snippets.value = snippets.value.filter(s => s.local_id !== targetId);
  editingLocalId.value = null;
}

function editSnippet(snippet: AnnotationDraft): void {
  drawerDraft.value = { ...snippet };
  drawerFieldType.value = deriveFieldTypeForSnippet(snippet);
  editingLocalId.value = snippet.local_id;
  drawerInitialHidden.value = hiddenIds.value.has(snippet.local_id);
  drawerVisible.value = true;
}

function removeSnippet(localId: string): void {
  snippets.value = snippets.value.filter(s => s.local_id !== localId);
  if (hiddenIds.value.has(localId)) {
    const next = new Set(hiddenIds.value);
    next.delete(localId);
    hiddenIds.value = next;
  }
}

// --- Copy ------------------------------------------------------------

async function copySnippet(snippet: AnnotationDraft): Promise<void> {
  const text = renderFieldSnippet(snippet);
  if (!text) return;
  try {
    // Feature-detect: the Clipboard API is absent in insecure contexts.
    if ('clipboard' in navigator) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      document.execCommand('copy');
      ta.remove();
    }
    flashCopied(snippet.local_id);
  } catch {
    message.error('Could not copy to clipboard.');
  }
}

async function copyControlFlowSnippet(snippet: ControlFlowSnippet): Promise<void> {
  const text = renderControlFlowSnippet(snippet);
  if (!text) return;
  try {
    // Feature-detect: the Clipboard API is absent in insecure contexts.
    if ('clipboard' in navigator) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      document.execCommand('copy');
      ta.remove();
    }
    flashCopied(snippet.local_id);
  } catch {
    message.error('Could not copy to clipboard.');
  }
}

function flashCopied(localId: string): void {
  copiedAt.value = { ...copiedAt.value, [localId]: Date.now() };
  if (copyFeedbackTimers[localId]) window.clearTimeout(copyFeedbackTimers[localId]);
  copyFeedbackTimers[localId] = window.setTimeout(() => {
    const { [localId]: _removed, ...rest } = copiedAt.value;
    void _removed;
    copiedAt.value = rest;
  }, 1500);
}

// --- Card label / badge helpers --------------------------------------

function snippetKindLabel(snippet: AnnotationDraft): string {
  const binding = snippet.binding;
  if (binding?.kind === 'entity') {
    const entity = catalog.value?.entities.find(e => e.name === binding.entity_type);
    return entity?.label ?? binding.entity_type;
  }
  if (binding?.kind === 'object') {
    return 'Group';
  }
  const fieldType = deriveFieldTypeForSnippet(snippet);
  return fieldType ? humanizeType(fieldType) : 'Field';
}

function snippetSubLabel(snippet: AnnotationDraft): string {
  const binding = snippet.binding;
  if (binding?.kind === 'entity' || binding?.kind === 'object') {
    return `${binding.instance_alias}.${binding.sub_field}`;
  }
  return snippet.field_name ?? 'field';
}

function humanizeType(type: TemplateFieldType): string {
  switch (type) {
    case TemplateFieldType.STRING:
      return 'Text';
    case TemplateFieldType.NUMBER:
      return 'Number';
    case TemplateFieldType.DATE:
      return 'Date';
    case TemplateFieldType.SELECTION:
      return 'Selection';
    case TemplateFieldType.BOOLEAN:
      return 'Yes / No';
    default:
      return type;
  }
}

const snippetCount = computed(() => snippets.value.length + controlFlowSnippets.value.length);
const hasSnippets = computed(() => snippetCount.value > 0);

/** Which chip family a saved snippet's badge takes — same palette the canvas
    chips use: green entity, blue object, violet primitive, amber logic. */
function snippetBadgeClass(snippet: AnnotationDraft): string {
  const kind = snippet.binding?.kind;
  if (kind === 'entity') return 'badge--entity';
  if (kind === 'object') return 'badge--object';
  return 'badge--custom';
}

// --- chrome ------------------------------------------------------------------
const fsMeta = gaiaFontSize.meta;
const fwBold = String(gaiaFontWeight.bold);
const surface = gaiaNeutral.surface;
const panelBorder = gaiaNeutral.borderDivider;
const pageX = gaiaShellPadding.pageX;
const cardBorder = gaiaNeutral.borderCard;
const codeSurface = gaiaNeutral.surfaceSoft;
const inkColor = gaiaNeutral.ink;
const monoFont = gaiaFonts.mono;
const successColor = gaiaStatus.successText;
const customBadgeBg = gaiaTemplating.customSurface;
const customBadgeFg = gaiaTemplating.customFg;
const entityBadgeBg = gaiaTemplating.entitySurface;
const entityBadgeFg = gaiaTemplating.entityFg;
const objectBadgeBg = gaiaTemplating.objectSurface;
const objectBadgeFg = gaiaTemplating.objectFg;
const logicBadgeBg = gaiaTemplating.blockSurface;
const logicBadgeFg = gaiaTemplating.blockFg;
</script>

<template>
  <Layout fill position="absolute" content-padding="0" content-max-width="100%">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.organisation }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.snippets }}</n-breadcrumb-item>
    </template>

    <n-flex vertical class="snippet-library" :size="0" :wrap="false">
      <header class="snippet-library__header">
        <n-flex vertical :size="5" :style="{ minWidth: 0 }">
          <gaia-header :level="1" variant="display" size="record" :style="{ margin: 0 }">
            Snippet library
          </gaia-header>
          <gaia-text size="row" tone="tertiary" :style="{ maxWidth: '640px' }">
            Build Jinja snippets and copy them to your clipboard. Nothing here is saved to a
            document — it's a personal scratchpad for this session.
          </gaia-text>
        </n-flex>
        <gaia-tag v-if="snippetCount > 0" round :style="{ marginLeft: 'auto', flex: 'none' }">
          {{ snippetCount }} snippet{{ snippetCount === 1 ? '' : 's' }}
        </gaia-tag>
      </header>

      <gaia-banner v-if="loadError" type="error" class="snippet-library__error">
        {{ loadError }}
      </gaia-banner>

      <n-flex
        v-else-if="loading"
        vertical
        align="center"
        justify="center"
        class="snippet-library__loading"
        :size="12"
      >
        <gaia-spinner size="medium" />
        <gaia-text size="row" tone="tertiary">Loading catalog…</gaia-text>
      </n-flex>

      <n-flex v-else class="snippet-library__body" :size="0" :wrap="false">
        <aside class="snippet-library__palette">
          <FieldPalette
            :catalog="catalog"
            :loading="loading"
            show-control-flow
            hint="Click a field on the left to configure it, then save it for clipboard use."
            @pick-primitive="handlePickPrimitive"
            @pick-entity-field="handlePickEntityField"
            @pick-object-field="handlePickObjectField"
            @pick-conditional="handlePickConditional"
            @pick-loop="handlePickLoop"
          />
        </aside>

        <main class="snippet-library__main">
          <gaia-empty-state
            v-if="!hasSnippets"
            class="snippet-library__empty"
            title="No snippets yet"
          >
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M6 2.5h8l4 4V21a.5.5 0 0 1-.5.5h-11A.5.5 0 0 1 6 21z" />
                <path d="M14 2.5v4h4" />
              </svg>
            </template>
            Click a field or logic tile on the left to start building.
          </gaia-empty-state>

          <ul v-else class="snippet-list">
            <li v-for="snippet in controlFlowSnippets" :key="snippet.local_id" class="snippet-card">
              <div class="snippet-card__head">
                <span class="badge badge--logic">{{ controlFlowKindLabel(snippet) }}</span>
                <span class="snippet-card__sub">{{ controlFlowSubLabel(snippet) }}</span>
                <div class="snippet-card__actions">
                  <gaia-icon-button
                    :dimension="26"
                    quaternary
                    :title="copiedAt[snippet.local_id] ? 'Copied' : 'Copy snippet'"
                    :class="['copy-action', { copied: copiedAt[snippet.local_id] }]"
                    @click="copyControlFlowSnippet(snippet)"
                  >
                    <svg
                      v-if="copiedAt[snippet.local_id]"
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M2.5 8.5 6 12l7.5-8" />
                    </svg>
                    <svg
                      v-else
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
                      <path d="M10.5 5.5v-2a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2" />
                    </svg>
                  </gaia-icon-button>
                  <gaia-button size="tiny" quaternary @click="editControlFlowSnippet(snippet)"
                    >Edit</gaia-button
                  >
                  <gaia-delete-button
                    subject="snippet"
                    @click="removeControlFlowSnippet(snippet.local_id)"
                  />
                </div>
              </div>
              <pre class="snippet-card__code">{{ renderControlFlowSnippet(snippet) }}</pre>
            </li>

            <li v-for="snippet in snippets" :key="snippet.local_id" class="snippet-card">
              <div class="snippet-card__head">
                <span class="badge" :class="snippetBadgeClass(snippet)">
                  {{ snippetKindLabel(snippet) }}
                </span>
                <span class="snippet-card__sub">{{ snippetSubLabel(snippet) }}</span>
                <div class="snippet-card__actions">
                  <gaia-icon-button
                    :dimension="26"
                    quaternary
                    :title="copiedAt[snippet.local_id] ? 'Copied' : 'Copy snippet'"
                    :class="['copy-action', { copied: copiedAt[snippet.local_id] }]"
                    @click="copySnippet(snippet)"
                  >
                    <svg
                      v-if="copiedAt[snippet.local_id]"
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M2.5 8.5 6 12l7.5-8" />
                    </svg>
                    <svg
                      v-else
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
                      <path d="M10.5 5.5v-2a1 1 0 0 0-1-1h-6a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2" />
                    </svg>
                  </gaia-icon-button>
                  <gaia-button size="tiny" quaternary @click="editSnippet(snippet)"
                    >Edit</gaia-button
                  >
                  <gaia-delete-button subject="snippet" @click="removeSnippet(snippet.local_id)" />
                </div>
              </div>
              <pre class="snippet-card__code">{{ renderFieldSnippet(snippet) }}</pre>
            </li>
          </ul>
        </main>
      </n-flex>

      <AnnotationConfigPopover
        v-model:show="drawerVisible"
        :annotation="drawerDraft"
        :reference="null"
        :filter-catalog="catalog?.filters ?? []"
        :entity-catalog="catalog?.entities ?? []"
        :field-type="drawerFieldType"
        :instances="sessionInstances"
        :preview-only="false"
        show-hidden
        :initial-hidden="drawerInitialHidden"
        @apply="handleApply"
        @remove="handleRemoveActive"
      />

      <ControlFlowConfigDrawer
        v-model:show="cfDrawerVisible"
        :snippet="cfDraft"
        :editing="cfEditingLocalId !== null"
        @apply="handleApplyControlFlow"
        @remove="handleRemoveActiveControlFlow"
      />
    </n-flex>
  </Layout>
</template>

<style scoped>
.snippet-library {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.snippet-library__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 22px v-bind(pageX) 16px;
  flex: none;
}

.snippet-library__error {
  margin: 0 v-bind(pageX) 16px;
}

.snippet-library__loading {
  flex: 1;
  min-height: 0;
}

.snippet-library__body {
  flex: 1;
  min-height: 0;
  border-top: 1px solid v-bind(panelBorder);
}

.snippet-library__palette {
  width: 280px;
  flex: none;
  min-height: 0;
  overflow: hidden;
}

.snippet-library__main {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 22px v-bind(pageX);
}

.snippet-library__empty {
  padding: 60px 0;
}

.snippet-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 760px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* The saved-snippet card: a labelled head strip over a mono code block. */
.snippet-card {
  border: 1px solid v-bind(cardBorder);
  border-radius: 11px;
  background: v-bind(surface);
  overflow: hidden;
}

.snippet-card__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px 9px 14px;
  border-bottom: 1px solid v-bind(panelBorder);
}

/* Kind badge, tinted by what the snippet binds to — the canvas chip palette. */
.badge {
  flex: none;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: v-bind(fsMeta);
  font-weight: v-bind(fwBold);
  background: v-bind(customBadgeBg);
  color: v-bind(customBadgeFg);
}

.badge--entity {
  background: v-bind(entityBadgeBg);
  color: v-bind(entityBadgeFg);
}

.badge--object {
  background: v-bind(objectBadgeBg);
  color: v-bind(objectBadgeFg);
}

.badge--logic {
  background: v-bind(logicBadgeBg);
  color: v-bind(logicBadgeFg);
}

.snippet-card__sub {
  font-size: v-bind(fsMeta);
  font-family: v-bind(monoFont);
  color: v-bind(inkColor);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.snippet-card__actions {
  margin-left: auto;
  flex: none;
  display: flex;
  align-items: center;
  gap: 4px;
}

.copy-action.copied {
  color: v-bind(successColor);
}

.snippet-card__code {
  margin: 0;
  padding: 13px 16px;
  background: v-bind(codeSurface);
  font-family: v-bind(monoFont);
  font-size: v-bind(fsMeta);
  line-height: 1.65;
  color: v-bind(inkColor);
  overflow-x: auto;
  white-space: pre;
}
</style>
